import { getEffectiveFlightPrice, getTotalStopCount } from 'lib/flights/flightUtils'
import { isResolvedFlightJourneyId } from './flightJourneyId'

export interface FlightPartyPricingContext {
  /** Exact selected fare total for the complete searched travelling party. */
  fareTotalPrice?: number
  /** Public total for the same selected fare and travelling party. */
  fareRegularTotalPrice?: number
  /** Currency of the exact selected fare total. */
  fareCurrencyCode?: string
}

interface SelectedFareTotalInput {
  departingJourney: App.JourneyV2
  returningJourney?: App.JourneyV2
  departingFareFamily?: App.FlightFareFamily
  returningFareFamily?: App.FlightFareFamily
}

export function buildFlightPartyPricingContext(
  fareTotalPrice: number | undefined,
  fareCurrencyCode: string | undefined,
  fareRegularTotalPrice?: number,
): FlightPartyPricingContext {
  if (
    typeof fareTotalPrice !== 'number' ||
    !Number.isFinite(fareTotalPrice) ||
    fareTotalPrice < 0 ||
    !fareCurrencyCode
  ) return {}
  return {
    fareTotalPrice,
    fareCurrencyCode,
    ...(fareRegularTotalPrice !== undefined && Number.isFinite(fareRegularTotalPrice) && fareRegularTotalPrice > fareTotalPrice
      ? { fareRegularTotalPrice }
      : {}),
  }
}

/**
 * Returns the exact all-traveller total represented by the selected one-way or
 * return fare. A missing fare-family selection falls back to the journey's
 * all-traveller price; a one-way journey is deliberately counted once.
 */
export function getSelectedFareTotal({
  departingJourney,
  returningJourney,
  departingFareFamily,
  returningFareFamily,
}: SelectedFareTotalInput): number | undefined {
  const departingTotal = departingFareFamily?.cost.all ?? getEffectiveFlightPrice(departingJourney).all.totalFare
  if (!Number.isFinite(departingTotal)) return undefined
  if (!returningJourney) return departingTotal

  const returningTotal = returningFareFamily?.cost.all ?? getEffectiveFlightPrice(returningJourney).all.totalFare
  if (!Number.isFinite(returningTotal)) return undefined
  return departingTotal + returningTotal
}

export function getSelectedFarePricing(input: SelectedFareTotalInput): FlightPartyPricingContext {
  const { departingJourney, returningJourney, departingFareFamily, returningFareFamily } = input
  if (returningJourney && returningJourney.currencyCode !== departingJourney.currencyCode) return {}
  const departingRegular = departingFareFamily
    ? departingFareFamily.regularCost?.all
    : departingJourney.regularPrice?.all.totalFare
  const returningRegular = returningFareFamily
    ? returningFareFamily.regularCost?.all
    : returningJourney?.regularPrice?.all.totalFare
  const isMemberFare = (departingFareFamily?.bookingInfo?.isLuxPlusFare ?? departingJourney.bookingInfo?.isLuxPlusFare) === true &&
    (!returningJourney || (returningFareFamily?.bookingInfo?.isLuxPlusFare ?? returningJourney.bookingInfo?.isLuxPlusFare) === true)
  const regularTotal = isMemberFare && departingRegular !== undefined && (!returningJourney || returningRegular !== undefined)
    ? departingRegular + (returningJourney ? returningRegular! : 0)
    : undefined
  return buildFlightPartyPricingContext(getSelectedFareTotal(input), departingJourney.currencyCode, regularTotal)
}

/**
 * Renders the per-journey context block sent to the assistant when a flight is
 * added to the trip. Shared by the flight search drawer and the chat flight
 * recommendation cards so the context format stays single-sourced.
 *
 * `fareFamily` is the fare the traveller picked in the "Select a fare" modal
 * before the flight was sent, when the journey offered a choice.
 */
export function buildFlightContext(
  journey: App.JourneyV2,
  fareFamily?: App.FlightFareFamily,
  pricing?: FlightPartyPricingContext,
): string {
  const jFlights = journey.flightGroup.flights
  const firstFlight = jFlights[0]
  const lastFlight = jFlights[jFlights.length - 1]
  const duration = journey.flightGroup.totalFlightDuration
  const lines = [
    `Carrier: ${journey.carrierName}`,
    `Flight: ${jFlights.map(f => f.flightNumber).filter(Boolean).join(' / ')}`,
    `From: ${firstFlight.departureAirport} (${firstFlight.departureAirportName})`,
    `To: ${lastFlight.arrivalAirport} (${lastFlight.arrivalAirportName})`,
    `Departure: ${firstFlight.departureDateTime}`,
    `Arrival: ${lastFlight.arrivalDateTime}`,
    `Duration: ${duration.hours}h ${duration.minutes}m`,
    `Stops: ${getTotalStopCount(jFlights)}`,
    `Price: ${journey.currencyCode} ${journey.price.adult.totalFare}`,
    `Fare type: ${journey.fareType}`,
    `Carrier logo: ${journey.carrierBrandMark ?? journey.carrierLogo}`,
  ]
  if (journey.regularPrice?.all?.totalFare !== undefined) lines.push(`Regular price: ${journey.currencyCode} ${journey.regularPrice.all.totalFare}`)
  if (journey.regularPrice?.adult?.totalFare !== undefined) lines.push(`Regular price per person: ${journey.currencyCode} ${journey.regularPrice.adult.totalFare}`)
  if (journey.bookingInfo?.isLuxPlusFare !== undefined) lines.push(`LuxPlus fare: ${journey.bookingInfo.isLuxPlusFare}`)
  if (journey.bookingInfo?.journeyKey) lines.push(`Journey Key: ${journey.bookingInfo.journeyKey}`)
  if (isResolvedFlightJourneyId(journey.id, journey.bookingInfo?.journeyKey)) lines.push(`Journey ID: ${journey.id}`)
  if (journey.bookingInfo?.providerSearchId) lines.push(`Search ID: ${journey.bookingInfo.providerSearchId}`)
  if (fareFamily) {
    lines.push(`Selected fare: ${fareFamily.fareFamily.name}`)
    lines.push(`Selected fare cabin: ${fareFamily.cabin}`)
    lines.push(`Selected fare ID: ${fareFamily.id}`)
  }
  if (pricing?.fareTotalPrice !== undefined && pricing.fareCurrencyCode) {
    lines.push(`Selected fare total for travelling party: ${pricing.fareCurrencyCode} ${pricing.fareTotalPrice}`)
    if (pricing.fareRegularTotalPrice !== undefined) {
      lines.push(`Selected fare public total for same travelling party (fareRegularTotalPrice): ${pricing.fareCurrencyCode} ${pricing.fareRegularTotalPrice}`)
    }
  }
  return lines.join('\n')
}
